import React,{useEffect,useMemo,useState} from "react";
import "./researchEcologyKanban.css";
import RepositoryAperture from "./RepositoryAperture";
import ResearchStateHistory from "./ResearchStateHistory";
import TranslationStream from "./TranslationStream";
import StateDissonance from "./StateDissonance";

const DATA_URL=`${process.env.PUBLIC_URL||""}/research-ops/research-ecology-board.json`;
const ORDER=["INBOX","READY","ACTIVE","WAITING","PARENT REVIEW","BLOCKED / ESCALATED","CLOSED / FROZEN"];

function bucket(status){return ORDER.includes(status)?status:"INBOX"}
function isWatch(card){return card.state_dissonance&&!String(card.state_dissonance).toUpperCase().startsWith("NONE")}
function label(k){return String(k).replaceAll("_"," ").replace(/\b\w/g,m=>m.toUpperCase())}

function Field({title,items,text,mono=false,tone=""}){
 const vals=Array.isArray(items)?items.filter(Boolean):[];
 if(!text&&!vals.length)return null;
 return <section className={`rek-inspect-field ${tone} ${mono?"mono":""}`}>
  <h4>{title}</h4>
  {text?<p>{text}</p>:<ul>{vals.map((x,i)=><li key={`${title}-${i}`}>{x}</li>)}</ul>}
 </section>
}

function PlanningCard({card,selected,onSelect}){
 return <button type="button" className={`rek-card ${selected?"selected":""} ${isWatch(card)?"watch":""}`} onClick={()=>onSelect(card.card_id)}>
  <div className="rek-card-top"><span className="rek-id">{card.card_id}</span><span className="rek-branch">{card.branch}</span></div>
  <h3>{card.title}</h3>
  {card.card_id==="E-A0-A1-B-R1"?<div className="rek-projection-thumb"><img src="/research-ecology/child-e/thumbnail.svg" alt="Child E H0 Theory Atlas projection thumbnail"/><span><b>H0 Theory Atlas</b><em>/research-ecology/child-e/</em></span></div>:null}
  <p>{card.purpose}</p>
  <div className="rek-card-bottom"><span>{card.status_detail||card.status}</span><b>Inspect →</b></div>
 </button>
}

function Inspector({card}){
 const[raw,setRaw]=useState(false);
 if(!card)return <aside className="rek-inspector empty"><h2>Select a card</h2><p>Choose any branch card to inspect its full operational contract.</p></aside>;
 return <aside className="rek-inspector">
  <header className="rek-inspector-head">
   <div><span className="rek-kicker">DEEP INSPECTOR · {card.card_id}</span><h2>{card.title}</h2><p>{card.branch} · {card.status_detail||card.status}</p></div>
   {isWatch(card)?<span className="rek-watch-badge">STATE WATCH</span>:<span className="rek-ok-badge">NO DISSONANCE</span>}
  </header>

  <section className="rek-authority-pair">
   <div><small>CURRENT AUTHORITY</small><b>{card.authority_owner}</b></div>
   <div><small>NEXT AUTHORITY</small><b>{card.next_authority}</b></div>
  </section>

  {card.card_id==="E-A0-A1-B-R1"?<a className="rek-projection-link" href="/research-ecology/child-e/" target="_blank" rel="noreferrer">Open H0 Theory Atlas <span>↗</span></a>:null}
  <Field title="Purpose" text={card.purpose}/>
  <Field title="Dependencies" items={card.dependencies}/>
  <div className="rek-two">
   <Field title="Authorized actions" items={card.authorized_actions} tone="allowed"/>
   <Field title="Forbidden actions" items={card.forbidden_actions} tone="forbidden"/>
  </div>
  <Field title="Stop condition" text={card.stop_condition} tone="stop"/>
  <Field title="Expected outputs" items={card.expected_outputs}/>
  <Field title="Artifact / handoff paths" items={card.artifact_handoff_paths} mono/>
  <Field title="Receipt / audit hashes" items={card.receipt_audit_hashes} mono/>
  <Field title="State dissonance" text={card.state_dissonance} tone={isWatch(card)?"warning":""}/>

  <div className="rek-inspect-footer">
   <span>Last update · {card.last_update||"unknown"}</span>
   <button type="button" onClick={()=>setRaw(v=>!v)}>{raw?"Hide raw card JSON":"Inspect raw card JSON"}</button>
  </div>
  {raw?<pre className="rek-raw">{JSON.stringify(card,null,2)}</pre>:null}
 </aside>
}

function Ledger({cards,onSelect}){
 return <section className="rek-ledger">
  <header><div><span className="rek-kicker">HANDOFF LEDGER</span><h2>Authority movement</h2></div><p>Administrative trace only — moving a card never creates scientific warrant.</p></header>
  <div className="rek-table-wrap"><table><thead><tr><th>Card</th><th>From / owner</th><th>Current state</th><th>Next authority</th><th>Stop condition</th><th>Artifacts</th></tr></thead>
  <tbody>{cards.map(c=><tr key={c.card_id} onClick={()=>onSelect(c.card_id)}>
   <td><b>{c.card_id}</b><span>{c.title}</span></td>
   <td>{c.authority_owner}</td><td>{c.status_detail||c.status}</td><td>{c.next_authority}</td><td>{c.stop_condition}</td>
   <td>{(c.artifact_handoff_paths||[]).join(" · ")||"—"}</td>
  </tr>)}</tbody></table></div>
 </section>
}

export default function ResearchEcologyKanban(){
 const[board,setBoard]=useState(null),[error,setError]=useState(""),[loaded,setLoaded]=useState(null);
 const[selectedId,setSelectedId]=useState("D-CONTEXT-PROV-R1"),[query,setQuery]=useState(""),[showClosed,setShowClosed]=useState(false);

 useEffect(()=>{let live=true;async function load(){try{const r=await fetch(`${DATA_URL}?t=${Date.now()}`,{cache:"no-store"});if(!r.ok)throw new Error(`${r.status} ${r.statusText}`);const j=await r.json();if(live){setBoard(j);setLoaded(new Date());setError("")}}catch(e){if(live)setError(e?.message||String(e))}}load();const t=setInterval(load,60000);return()=>{live=false;clearInterval(t)}},[]);

 const cards=board?.cards||[];
 const selected=cards.find(c=>c.card_id===selectedId)||cards[0]||null;
 const filtered=useMemo(()=>cards.filter(c=>{
  if(!showClosed&&c.status==="CLOSED / FROZEN")return false;
  const q=query.trim().toLowerCase();if(!q)return true;
  return JSON.stringify(c).toLowerCase().includes(q);
 }),[cards,query,showClosed]);
 const grouped=useMemo(()=>{const g=Object.fromEntries(ORDER.map(x=>[x,[]]));filtered.forEach(c=>g[bucket(c.status)].push(c));return g},[filtered]);

 if(!board)return <main className="rek-page"><div className="rek-loading">{error?`Research Ecology board unavailable · ${error}`:"Loading Research Ecology board…"}</div></main>;

 const active=cards.filter(c=>c.status==="ACTIVE").length;
 const waiting=cards.filter(c=>c.status==="WAITING").length;
 const watches=cards.filter(isWatch).length;

 return <main className="rek-page">
  <header className="rek-appbar">
   <a href="/" className="rek-back">← Needs Analysis</a>
   <div><span>CHILD B-OPS</span><b>Research Planning</b></div>
   <div className="rek-path">/planning</div>
  </header>

  <section className="rek-hero">
   <div><span className="rek-kicker">RESEARCH OPERATIONS · ADMINISTRATIVE MIRROR</span><h1>Research Ecology<br/><em>Planning Board</em></h1><p>One inspectable view of branch state, handoffs, dependencies, stop conditions, receipts and current authority. This page tracks the ecology; it does not decide scientific meaning.</p></div>
   <div className="rek-metrics"><span><b>{active}</b>Active</span><span><b>{waiting}</b>Waiting</span><span><b>{watches}</b>State watches</span><span><b>{cards.length}</b>Total cards</span></div>
  </section>

  <div className="rek-guards">{board.authority.guards.map(x=><span key={x}>{x}</span>)}</div>

  <section className="rek-gate">
   <header><span className="rek-kicker">CURRENT PARENT DISPOSITION</span><h2>{board.parent_disposition.program}</h2></header>
   <div className="rek-gate-grid">{Object.entries(board.parent_disposition).filter(([k])=>k!=="program").map(([k,v])=><div key={k}><small>{label(k)}</small><b>{v}</b></div>)}</div>
  </section>

  <section className="rek-warning">
   <div><span className="rek-kicker">NEGATIVE INSTRUMENTATION EVIDENCE</span><h2>Preserve the inflated availability readings</h2><p>{board.instrumentation_warning.interpretation}</p></div>
   <div className="rek-warning-values">{Object.entries(board.instrumentation_warning.values).map(([k,v])=><span key={k}><small>{k}</small><b>{v}</b></span>)}</div>
  </section>

  <section className="rek-workspace">
   <div className="rek-board-side">
    <div className="rek-tools"><label><span>Search board</span><input value={query} onChange={e=>setQuery(e.target.value)} placeholder="branch, receipt, artifact, authority…"/></label><button onClick={()=>setShowClosed(v=>!v)}>{showClosed?"Hide frozen":"Show frozen"}</button></div>
    <div className="rek-board">
     {ORDER.filter(col=>col!=="CLOSED / FROZEN"||showClosed).filter(col=>grouped[col]?.length).map(col=><section className="rek-column" key={col}><header><b>{col}</b><span>{grouped[col].length}</span></header><div>{grouped[col].map(c=><PlanningCard card={c} selected={selected?.card_id===c.card_id} onSelect={setSelectedId} key={c.card_id}/>)}</div></section>)}
    </div>
   </div>
   <Inspector card={selected}/>
  </section>

  <RepositoryAperture />

  <ResearchStateHistory />

  <TranslationStream />

  <StateDissonance />

  <Ledger cards={cards} onSelect={setSelectedId}/>

  <footer className="rek-footer"><div><b>Board revision</b><span>{board.board_revision}</span></div><div><b>Snapshot</b><span>{board.date}</span></div><div><b>Local refresh</b><span>60 seconds{loaded?` · last ${loaded.toLocaleTimeString()}`:""}</span></div>{error?<div><b>Refresh warning</b><span>{error}</span></div>:null}</footer>
 </main>
}
